import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { today } from "../utils/dateUtils";
import LeaveHistory from "../components/LeaveHistory";
import AttendanceTable from "../components/AttendanceTable";
import LeaveModal from "../components/LeaveModal";
import CheckInCheckOutCard from "../components/CheckInCheckOutCard";
import ExpenseModal from "../components/ExpenseModal";
import { SalaryInfoModal, SalarySlipModal } from "../components/SalarySlipModal";
import { SERVER_URL } from "../config";
import Sidebar from "../components/Sidebar";
import {
  Bars3Icon,
  ClockIcon,
  CalendarDaysIcon,
  TableCellsIcon,
  BanknotesIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";

const toDateStr = (date) => date.toISOString().split("T")[0];

const TABS = [
  { key: "attendance", label: "Attendance",    icon: TableCellsIcon },
  { key: "leaves",     label: "Leave History", icon: CalendarDaysIcon },
];

/* ════════════════════════════════════════════════════════════════════════════ */
const AttendancePage = () => {
  useNavigate();
  const employee = JSON.parse(localStorage.getItem("employee"));
  if (!employee) { window.location.href = "/login"; return null; }

  const [activeTab, setActiveTab]                     = useState("attendance");
  const [allRecords, setAllRecords]                   = useState([]);
  const [isLoading, setIsLoading]                     = useState(false);
  const [fetchError, setFetchError]                   = useState("");
  const [leaveHistoryData, setLeaveHistoryData]       = useState([]);
  const [leaveLoading, setLeaveLoading]               = useState(false);
  const [showLeaveModal, setShowLeaveModal]           = useState(false);
  const [isLeaveSubmitting, setIsLeaveSubmitting]     = useState(false);
  const [leaveStatus, setLeaveStatus]                 = useState("");
  const [leaveData, setLeaveData]                     = useState("");
  const [isSidebarOpen, setIsSidebarOpen]             = useState(false);
  const [showExpenseModal, setShowExpenseModal]       = useState(false);
  const [showSalaryInfoModal, setShowSalaryInfoModal] = useState(false);
  const [showSalarySlipModal, setShowSalarySlipModal] = useState(false);
  const [selectedSlipId, setSelectedSlipId]           = useState(null);

  const isOnLeaveToday = useMemo(() => {
    const todayISO = new Date().toISOString().slice(0, 10);
    return (leaveHistoryData ?? []).some((leave) => {
      const from   = String(leave.start_date ?? "").slice(0, 10);
      const to     = String(leave.end_date   ?? "").slice(0, 10);
      const status = String(leave.status     ?? "").toLowerCase();
      return from && to && todayISO >= from && todayISO <= to &&
             (status === "pending" || status === "approved");
    });
  }, [leaveHistoryData]);

  const fetchAttendanceHistory = useCallback(async () => {
    setIsLoading(true);
    setFetchError("");
    try {
      const yearStart = `${new Date().getFullYear()}-01-01`;
      const res = await axios.post(`${SERVER_URL}/DataFetch/attendanceHistory.php`, {
        employee_id:   employee.employee_id   ?? null,
        employee_name: employee.employee_name ?? null,
        start_date:    yearStart,
        end_date:      toDateStr(new Date()),
      });
      if (res.data.status === true) {
        setAllRecords(res.data.records ?? []);
      } else {
        setFetchError(res.data.message ?? "Failed to fetch attendance.");
      }
    } catch (err) {
      console.error("Error fetching attendance:", err);
      setFetchError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [employee.employee_id, employee.employee_name]);

  const fetchLeaveHistory = useCallback(async (month, year) => {
    setLeaveLoading(true);
    try {
      const m   = month ?? new Date().getMonth() + 1;
      const y   = year  ?? new Date().getFullYear();
      const pad = (n) => String(n).padStart(2, "0");
      const startDate = `${y}-${pad(m)}-01`;
      const lastDay   = new Date(y, m, 0).getDate();
      const endDate   = `${y}-${pad(m)}-${lastDay}`;
      const res = await axios.post(
        `${SERVER_URL}/Rahul/fetchEmployeeWiseLeaveHistory.php`,
        { employeeId: employee.employee_id, startDate, endDate }
      );
      setLeaveHistoryData(res.data.status === "success" ? res.data.leaves : []);
    } catch (err) {
      console.error("Error fetching leave history:", err);
      setLeaveHistoryData([]);
    } finally {
      setLeaveLoading(false);
    }
  }, [employee.employee_id]);

  useEffect(() => {
    fetchAttendanceHistory();
    fetchLeaveHistory(new Date().getMonth() + 1, new Date().getFullYear());
  }, [fetchAttendanceHistory, fetchLeaveHistory]);

  const submitLeaveApplication = async () => {
    setIsLeaveSubmitting(true);
    setLeaveStatus("");
    setTimeout(() => {
      setLeaveStatus("Leave application submitted successfully!");
      setTimeout(() => {
        setShowLeaveModal(false);
        setIsLeaveSubmitting(false);
        fetchLeaveHistory();
      }, 1000);
    }, 1200);
  };

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .page-font { font-family: 'DM Sans', sans-serif; }
        .page-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
        .page-scroll::-webkit-scrollbar-track { background: transparent; }
        .page-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
        .page-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .header-gradient { background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%); }
        .tab-active-bar { background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); }
        .action-btn-expense { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); }
        .action-btn-salary { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); }
        .top-accent { background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%); }
      `}</style>

      {/* Top accent */}
      <div className="fixed top-0 left-0 right-0 h-0.5 z-50 top-accent" />

      <div className="flex h-screen overflow-hidden page-font" style={{ background: "#f5f7ff" }}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">

          {/* ── STICKY HEADER ── */}
          <header className="sticky top-0 z-20 border-b border-indigo-100/60 header-gradient shadow-sm shadow-indigo-100/50">
            <div className="px-3 sm:px-5 lg:px-7 py-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="flex-shrink-0 p-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-all border border-indigo-100"
                >
                  <Bars3Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-md shadow-indigo-200">
                    <ClockIcon className="w-4 h-4 text-white" />
                  </div>
                  <h1 className="text-sm sm:text-base lg:text-lg font-bold text-slate-800 tracking-tight truncate">
                    Attendance Dashboard
                  </h1>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-1.5 sm:gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowExpenseModal(true)}
                  className="action-btn-expense flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-emerald-700 border border-emerald-200 hover:border-emerald-300 transition-all"
                >
                  <BanknotesIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="hidden xs:inline sm:inline">Expense</span>
                </button>
                {/* <button
                  onClick={() => setShowSalaryInfoModal(true)}
                  className="action-btn-salary flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-blue-700 border border-blue-200 hover:border-blue-300 transition-all"
                >
                  <CreditCardIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="hidden xs:inline sm:inline">Pay Slip</span>
                </button> */}
              </div>
            </div>
          </header>

          {/* ── SCROLLABLE CONTENT ── */}
          <div className="flex-1 overflow-y-auto page-scroll">
            <div className="p-3 sm:p-4 lg:p-5 max-w-7xl mx-auto flex flex-col gap-3 sm:gap-4">

              {/* Check-in card */}
              <CheckInCheckOutCard
                today={today}
                error=""
                isLoading={isLoading}
                setShowLeaveModal={setShowLeaveModal}
                employeeId={employee.employee_id}
                employeeName={employee.employee_name}
                SERVER_URL={SERVER_URL}
                isOnLeaveToday={isOnLeaveToday}
              />

              {/* Tabs + Table section */}
              <div className="rounded-2xl overflow-hidden bg-white border border-indigo-100/60 shadow-sm shadow-indigo-100/40">
                {/* Tab bar */}
                <div className="flex border-b border-slate-100 bg-slate-50/80 overflow-x-auto">
                  {TABS.map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-3.5 text-xs sm:text-sm font-semibold transition-all duration-200 border-b-2 whitespace-nowrap flex-shrink-0 ${
                        activeTab === key
                          ? "border-indigo-500 text-indigo-700 bg-white"
                          : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-white/60"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                <div className="p-3 sm:p-4 lg:p-5 bg-white">
                  {activeTab === "attendance" && (
                    <AttendanceTable
                      data={allRecords}
                      isLoading={isLoading}
                      error={fetchError}
                      fetchAttendance={fetchAttendanceHistory}
                    />
                  )}
                  {activeTab === "leaves" && (
                    <LeaveHistory
                      leaveData={leaveHistoryData}
                      isLoading={leaveLoading}
                      onMonthChange={fetchLeaveHistory}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showLeaveModal && (
          <LeaveModal
            leaveData={leaveData}
            setLeaveData={setLeaveData}
            today={today}
            submitLeaveApplication={submitLeaveApplication}
            isLoading={isLeaveSubmitting}
            leaveStatus={leaveStatus}
            setShowLeaveModal={setShowLeaveModal}
          />
        )}
        {showExpenseModal && (
          <ExpenseModal isOpen={showExpenseModal} onClose={() => setShowExpenseModal(false)} employee={employee} />
        )}
        {/* {showSalaryInfoModal && (
          <SalaryInfoModal
            isOpen={showSalaryInfoModal}
            onClose={() => setShowSalaryInfoModal(false)}
            employee={employee}
            onViewSlip={(id) => { setSelectedSlipId(id); setShowSalarySlipModal(true); }}
          />
        )} */}
        {/* {showSalarySlipModal && selectedSlipId && (
          <SalarySlipModal slipId={selectedSlipId} onClose={() => setShowSalarySlipModal(false)} />
        )} */}
      </div>
    </>
  );
};

export default AttendancePage;